import FormInputComponent from "@via/components/inputs/form-input-component";
import Modal from "@via/components/modal";
import { useCreateVmetadataTracker } from "@via/pages/projects/projects.api.tsx";
import { ModalSize } from "@via/utils/constants";
import { vmetaNameValidation } from "@via/utils/form-validators";
import { type FC } from "react";
import { useForm } from "react-hook-form";

interface CreateMetadataTrackerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
  updateProject: (payload: {
    id: string;
    data: {
      vmetaId?: string;
    };
  }) => void;
}

const CreateMetadataTrackerModal: FC<CreateMetadataTrackerModalProps> = ({
  open,
  setOpen,
  projectId = "",
  updateProject,
}) => {
  const { mutateAsync: createVmetadataTracker, isPending: isLoading } =
    useCreateVmetadataTracker();

  const { control, handleSubmit } = useForm({
    mode: "onChange",
  });

  const handleCreateVmetadataTracker = async payload => {
    const res = await createVmetadataTracker(payload);

    if (res?.status === "success") {
      updateProject({
        id: projectId,
        data: {
          vmetaId: res?.data?._id,
        },
      });
    }
  };

  // Modal closes on true and stays open on false (invalid form) or a rejection (failed create,
  // which the hook reports).
  const handleConfirmModal = async (): Promise<boolean> => {
    let created = false;
    await handleSubmit(async payload => {
      await handleCreateVmetadataTracker(payload);
      created = true;
    })();
    return created;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="New Metadata Tracker"
      isConfirmLoading={isLoading}
      size={ModalSize.Large}
      onConfirm={handleConfirmModal}>
      <form onSubmit={e => e.preventDefault()}>
        <>
          <div className="space-y-6 pb-5 pt-2">
            <FormInputComponent
              control={control}
              name="name"
              label="Name (used in API URLs)"
              placeholder="e.g. rna_seq_v1"
              showTooltipIcon
              tooltipText={
                'Used in API routes and URLs.<br/>Letters/numbers/spaces/-/_ only. No dots.<br/>Spaces become "_" and uppercase becomes lowercase.'
              }
              validations={vmetaNameValidation("Name (used in API URLs)")}
              showRequiredField
            />
            <FormInputComponent
              control={control}
              name="label"
              label="Label (Display name)"
              placeholder="e.g. RNA-seq (v1)"
              showTooltipIcon
              tooltipText={
                "Shown in the UI (tabs, dropdowns). Any characters allowed."
              }
              showRequiredField
              validations={{
                required: "Label is required",
              }}
            />
          </div>
        </>
      </form>
    </Modal>
  );
};

export default CreateMetadataTrackerModal;
